import React, { useRef, useState } from 'react';
import { Upload, Camera, Trash2, Loader2, MapPin, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const MAX_W = 1280;
      const ratio = Math.min(1, MAX_W / img.width);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      const sizeBytes = Math.round((dataUrl.length * 3) / 4);
      resolve({ base64: dataUrl, fileSize: sizeBytes });
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const captureGps = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => resolve(null),
    { timeout: 5000, enableHighAccuracy: false }
  );
});

const FormatHint = () => (
  <div className="text-[10px] text-slate-400 mt-2 text-center">
    Formats acceptés : JPG, PNG · Max. 10 Mo
  </div>
);

/**
 * Slot de capture de photo pour 1 emplacement (style maquette).
 *
 * Cas vide : grande zone dropzone bordure dashed, icône upload vert,
 *            texte "Capturer ou importer", "Glissez-déposez un fichier ici",
 *            bouton "📷 Capturer ou importer".
 * Cas remplie : preview image + bouton remplacer + bouton supprimer.
 */
const PhotoSlot = ({ label, requiredBadge, recommendedBadge, existing, onUpload, onRemove }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Fichier trop lourd', description: 'Maximum 10 Mo', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const [{ base64, fileSize }, gps] = await Promise.all([
        compressImage(file), captureGps(),
      ]);
      await onUpload({ label, base64, fileSize, gpsCoords: gps });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.message || 'Échec compression', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const displayName =
    label === 'marchandise' ? 'Marchandise / colis' :
    label === 'vue_ensemble' ? "Vue d'ensemble" :
    label === 'bon_societe' ? 'Bon papier société' :
    label;

  return (
    <div className="space-y-1" data-testid={`photo-slot-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-slate-800">
          {displayName}
        </h4>
        {requiredBadge && !existing && (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100 text-[10px] uppercase tracking-wide">
            Obligatoire
          </Badge>
        )}
        {recommendedBadge && !existing && (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50 text-[10px] uppercase tracking-wide">
            Recommandé
          </Badge>
        )}
        {existing && (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px]">
            Capturée
          </Badge>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`photo-input-${label}`}
      />
      {existing ? (
        <div className="border-2 border-emerald-200 rounded-lg overflow-hidden bg-emerald-50/30 p-3 space-y-3">
          <img src={existing.base64} alt={label}
               className="w-full h-44 sm:h-52 object-cover rounded"
               data-testid={`photo-preview-${label}`} />
          {existing.gpsCoords && (
            <div className="text-[10px] text-slate-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {existing.gpsCoords.lat.toFixed(4)}, {existing.gpsCoords.lng.toFixed(4)}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 h-8 text-xs"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`photo-replace-${label}`}>
              {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
              Remplacer
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs text-red-600 hover:bg-red-50"
                    onClick={() => onRemove(existing.id)}
                    data-testid={`photo-remove-${label}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 sm:p-8 cursor-pointer transition-all
            ${dragOver
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/50'}
          `}
          data-testid={`photo-dropzone-${label}`}
        >
          <div className="flex flex-col items-center justify-center text-center space-y-3 min-h-[160px]">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Upload className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-700">Capturer ou importer</div>
              <div className="text-xs text-slate-400 mt-1">
                Glissez-déposez un fichier ici<br />
                ou utilisez le bouton ci-dessous
              </div>
            </div>
            <Button variant="outline" size="sm"
                    onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                    disabled={uploading}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`photo-cta-${label}`}>
              {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
              Capturer ou importer
            </Button>
          </div>
          <FormatHint />
        </div>
      )}
    </div>
  );
};

/**
 * Composant d'upload photos pro avec dropzone style maquette.
 * - 2 slots obligatoires (marchandise + vue_ensemble) en grandes cartes
 * - Section "Photos additionnelles (facultatif)" avec bouton "+ Ajouter des photos"
 * - Compteur "X / 2 obligatoires"
 */
const PhotoUploadCard = ({
  photos = [], requiredLabels = ['marchandise', 'vue_ensemble'],
  recommendedLabels = ['bon_societe'],
  maxPhotos = 10, onUpload, onRemove,
  blockingMessage = "Ajoutez les 2 photos obligatoires avant de passer à la signature.",
}) => {
  const optionalInputRef = useRef(null);
  const [uploadingOptional, setUploadingOptional] = useState(false);

  const allFixedLabels = [...requiredLabels, ...recommendedLabels];
  const requiredFilled = requiredLabels.filter(l => photos.some(p => p.label === l)).length;
  const optionalPhotos = photos.filter(p => !allFixedLabels.includes(p.label));
  const allRequiredOk = requiredFilled === requiredLabels.length;
  const totalCount = photos.length;
  const canAddOptional = totalCount < maxPhotos;

  const handleOptionalFile = async (file) => {
    if (!file) return;
    if (totalCount >= maxPhotos) {
      toast({ title: 'Limite atteinte', description: `Max ${maxPhotos} photos`, variant: 'destructive' });
      return;
    }
    setUploadingOptional(true);
    try {
      const [{ base64, fileSize }, gps] = await Promise.all([
        compressImage(file), captureGps(),
      ]);
      await onUpload({ label: 'autre', base64, fileSize, gpsCoords: gps });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.message || 'Échec compression', variant: 'destructive' });
    } finally { setUploadingOptional(false); }
  };

  return (
    <div className="space-y-5" data-testid="photo-upload-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Photos preuves</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Capturez au minimum les {requiredLabels.length} photos obligatoires. Le bon papier société est recommandé si fourni par le client.
          </p>
        </div>
        <Badge className={`text-xs font-medium ${
          allRequiredOk
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-orange-50 text-orange-700 border-orange-200'
        }`}>
          {requiredFilled} / {requiredLabels.length} obligatoires
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {requiredLabels.map((label) => {
          const existing = photos.find(p => p.label === label);
          return (
            <PhotoSlot
              key={label}
              label={label}
              requiredBadge
              existing={existing}
              onUpload={onUpload}
              onRemove={onRemove}
            />
          );
        })}
        {recommendedLabels.map((label) => {
          const existing = photos.find(p => p.label === label);
          return (
            <PhotoSlot
              key={label}
              label={label}
              recommendedBadge
              existing={existing}
              onUpload={onUpload}
              onRemove={onRemove}
            />
          );
        })}
      </div>

      {/* Photos additionnelles */}
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-sm font-medium text-slate-800">
              Photos additionnelles <span className="text-slate-400 font-normal">(facultatif)</span>
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Ajoutez d'autres photos si nécessaire (jusqu'à 5 fichiers).
            </p>
          </div>
          <input ref={optionalInputRef} type="file" accept="image/*" capture="environment"
                 className="hidden"
                 onChange={(e) => handleOptionalFile(e.target.files?.[0])}
                 data-testid="photo-input-optional" />
          {canAddOptional && (
            <Button variant="outline" size="sm"
                    onClick={() => optionalInputRef.current?.click()}
                    disabled={uploadingOptional}
                    className="border-emerald-300 text-emerald-700"
                    data-testid="photo-add-optional">
              {uploadingOptional
                ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                : <Plus className="w-3 h-3 mr-1" />}
              Ajouter des photos
            </Button>
          )}
        </div>
        {optionalPhotos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-3">
            {optionalPhotos.map(p => (
              <div key={p.id} className="relative group">
                <img src={p.base64} alt={p.label}
                     className="w-full h-20 object-cover rounded border border-slate-200" />
                <button
                  type="button"
                  onClick={() => onRemove(p.id)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`photo-remove-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message bloquant */}
      {!allRequiredOk && (
        <div className="border-2 border-dashed border-orange-300 bg-orange-50/50 rounded-lg p-3 flex items-start gap-2"
             data-testid="photo-blocking-msg">
          <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-orange-600 text-xs font-bold">!</span>
          </div>
          <span className="text-sm text-orange-700">{blockingMessage}</span>
        </div>
      )}
    </div>
  );
};

export default PhotoUploadCard;
